import {
    AccordionGroup,
    AccordionItem,
    AccordionPanel,
    AccordionTrigger
} from "@solomon-apps/shared/src/components/ui/accordion-group";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React from "react";

import type { WizardStep } from "../steps";

/* ── Left column — step accordion (progress overview) ───────────────── */

interface StepAccordionProps {
    steps: WizardStep[];
    /** The current flow step. */
    active: number;
    /** Jump back to a completed step (`index < active`). */
    onSelectStep: (index: number) => void;
    /** Steps left out of the list (by `steps` index); the rest number 1..n. */
    hidden?: (index: number) => boolean;
}

/**
 * The left numbered step list — a config of the shared `AccordionGroup`
 * (single-open, controlled by `active`, ember-dot indicator). The stepper's
 * 3-state accents (ember active numeral, imprint-dimmed future steps, neutral
 * completed) layer on per row via the trigger's `marker` override + title
 * colour, since the base marker is intentionally tone-neutral.
 *
 * Behaviour: the active step is open and not re-clickable; completed steps
 * (`index < active`) are clickable to jump back; future steps are disabled.
 * Disabling the active row too (it's force-opened by the controlled `value`)
 * is what makes it open-but-inert. A `hidden` step is left out and the
 * numbering closes over it.
 */
export function StepAccordion({
    steps,
    active,
    onSelectStep,
    hidden
}: StepAccordionProps): React.ReactElement {
    return (
        <AccordionGroup
            type="single"
            collapsible={false}
            indicator="dot"
            value={String(active)}
            onValueChange={(value) => {
                // Only completed (enabled) rows fire this; jump back to it.
                const next = typeof value === "string" ? value : value[0];
                if (next && Number(next) < active) onSelectStep(Number(next));
            }}
            className="border-t border-line-base xl:flex-1"
        >
            {steps.map((step, index) => {
                if (hidden?.(index)) return null;
                // Display number: the step's place among the shown steps.
                const number =
                    steps
                        .slice(0, index)
                        .filter((_, earlier) => !hidden?.(earlier)).length + 1;
                const open = index === active;
                const locked = index > active;
                const numberColor = open
                    ? "text-ember"
                    : locked
                      ? "text-foreground-imprint"
                      : "text-foreground-neutral";
                const titleColor = open
                    ? "text-foreground-base"
                    : locked
                      ? "text-foreground-imprint"
                      : "text-foreground-muted";
                return (
                    <AccordionItem
                        key={step.title}
                        value={String(index)}
                        // Active + future are inert; only completed rows click.
                        disabled={index >= active}
                    >
                        <AccordionTrigger
                            marker={
                                <Text
                                    variant="number"
                                    render={<span />}
                                    aria-hidden
                                    className={`shrink-0 w-5 tabular-nums ${numberColor}`}
                                >
                                    {number}
                                </Text>
                            }
                        >
                            <Text
                                variant="h3"
                                render={<span />}
                                className={`truncate ${titleColor}`}
                            >
                                {step.title}
                            </Text>
                        </AccordionTrigger>
                        {/* Detail aligns under the title: trigger px-2 (8) +
                            number col (w-5 = 20) + gap-3 (12) = 40px. */}
                        <AccordionPanel className="pl-10">
                            <Text
                                variant="small"
                                className="text-foreground-muted"
                            >
                                {step.detail}
                            </Text>
                        </AccordionPanel>
                    </AccordionItem>
                );
            })}
        </AccordionGroup>
    );
}
